import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Activity, Users, Globe2, Mail, LogOut, LayoutDashboard,
  Zap, Search, ChevronLeft, ChevronRight, X, RefreshCw,
  Monitor, Smartphone, TabletSmartphone, Eye, HardDrive,
  MapPin, Chrome, Clock,
} from 'lucide-react'
import { api, clearToken, getToken } from '../lib/api'

type Tab = 'overview' | 'events' | 'visitors' | 'gmail' | 'countries' | 'live'

export default function AdminDashboard() {
  const nav = useNavigate()
  const [tab, setTab] = useState<Tab>('overview')
  const [user, setUser] = useState<any>(null)

  useEffect(() => {
    if (!getToken()) {
      nav('/admin/login')
      return
    }
    const u = localStorage.getItem('cs_admin_user')
    if (u) setUser(JSON.parse(u))
    api.me().then(setUser).catch(() => nav('/admin/login'))
  }, [nav])

  const handleLogout = () => {
    clearToken()
    nav('/admin/login')
  }

  return (
    <div className="min-h-screen bg-void-black text-star-white">
      <div className="fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute -top-40 -left-40 h-[30rem] w-[30rem] rounded-full bg-nebula-purple/10 blur-3xl" />
        <div className="absolute bottom-0 right-0 h-[30rem] w-[30rem] rounded-full bg-cosmic-blue/10 blur-3xl" />
      </div>

      {/* Sidebar */}
      <aside className="fixed inset-y-0 left-0 z-30 w-64 border-r border-white/5 bg-void-black/80 backdrop-blur-xl">
        <div className="flex h-16 items-center gap-3 border-b border-white/5 px-6">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-cosmic">
            <HardDrive className="h-5 w-5 text-white" />
          </div>
          <div>
            <div className="text-sm font-bold gradient-text">Cloudsize</div>
            <div className="text-xs text-star-white/40">Admin Panel</div>
          </div>
        </div>

        <nav className="flex flex-col gap-1 p-3">
          <NavItem icon={LayoutDashboard} label="Overview" active={tab === 'overview'} onClick={() => setTab('overview')} />
          <NavItem icon={Zap} label="Live Feed" active={tab === 'live'} onClick={() => setTab('live')} badge="LIVE" />
          <NavItem icon={Activity} label="All Events" active={tab === 'events'} onClick={() => setTab('events')} />
          <NavItem icon={Users} label="Visitors" active={tab === 'visitors'} onClick={() => setTab('visitors')} />
          <NavItem icon={Mail} label="Gmail Logins" active={tab === 'gmail'} onClick={() => setTab('gmail')} />
          <NavItem icon={Globe2} label="Countries" active={tab === 'countries'} onClick={() => setTab('countries')} />
        </nav>

        <div className="absolute bottom-0 left-0 right-0 border-t border-white/5 p-4">
          {user && (
            <div className="mb-3 flex items-center gap-3 rounded-xl bg-white/5 p-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-cosmic text-xs font-bold">
                {(user.username || 'A')[0].toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-semibold">{user.full_name || user.username}</div>
                <div className="truncate text-xs text-star-white/40">
                  {user.is_admin ? 'Administrator' : 'User'}
                </div>
              </div>
            </div>
          )}
          <button
            onClick={handleLogout}
            className="flex w-full items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-4 py-2 text-sm font-medium text-star-white/70 hover:bg-white/10 transition"
          >
            <LogOut className="h-4 w-4" />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="ml-64 min-h-screen">
        <header className="sticky top-0 z-20 flex h-16 items-center justify-between border-b border-white/5 bg-void-black/80 px-8 backdrop-blur-xl">
          <h1 className="text-xl font-bold capitalize">
            {tab === 'gmail' ? 'Gmail Logins' : tab}
          </h1>
          <div className="flex items-center gap-3 text-xs text-star-white/50">
            <span className="flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-green-400 animate-pulse" />
              Connected to Railway
            </span>
          </div>
        </header>

        <div className="p-8">
          {tab === 'overview' && <OverviewTab />}
          {tab === 'live' && <LiveTab />}
          {tab === 'events' && <EventsTab />}
          {tab === 'visitors' && <VisitorsTab />}
          {tab === 'gmail' && <GmailTab />}
          {tab === 'countries' && <CountriesTab />}
        </div>
      </main>
    </div>
  )
}

function NavItem({
  icon: Icon, label, active, onClick, badge,
}: { icon: any; label: string; active: boolean; onClick: () => void; badge?: string }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center justify-between gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition ${
        active
          ? 'bg-gradient-to-r from-nebula-purple/20 to-cosmic-blue/10 text-star-white border border-nebula-purple/30'
          : 'text-star-white/60 hover:bg-white/5 hover:text-star-white'
      }`}
    >
      <div className="flex items-center gap-3">
        <Icon className={`h-4 w-4 ${active ? 'text-nebula-purple' : ''}`} />
        <span>{label}</span>
      </div>
      {badge && (
        <span className="rounded-full bg-red-500/20 px-2 py-0.5 text-[10px] font-bold text-red-400 animate-pulse">
          {badge}
        </span>
      )}
    </button>
  )
}

// ============================================================
// Overview Tab
// ============================================================
function OverviewTab() {
  const [stats, setStats] = useState<any>(null)
  const [loading, setLoading] = useState(true)

  const load = () => {
    setLoading(true)
    api.stats().then(setStats).finally(() => setLoading(false))
  }
  useEffect(() => { load() }, [])

  if (loading && !stats) return <Skeleton />

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-sm text-star-white/60">Real-time analytics for your Cloudsize app</p>
        <button onClick={load} className="flex items-center gap-2 rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-xs hover:bg-white/10 transition">
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Total Events" value={stats?.total_events ?? 0} icon={Activity} color="purple" />
        <StatCard label="Unique Visitors" value={stats?.unique_visitors ?? 0} icon={Users} color="cyan" />
        <StatCard label="Sessions" value={stats?.total_sessions ?? 0} icon={Zap} color="pink" />
        <StatCard label="Countries" value={stats?.countries ?? 0} icon={Globe2} color="amber" />
        <StatCard label="Events (24h)" value={stats?.events_24h ?? 0} icon={Clock} color="emerald" />
        <StatCard label="Events (1h)" value={stats?.events_1h ?? 0} icon={Clock} color="emerald" />
        <StatCard label="Gmail Logins" value={stats?.gmail_logins ?? 0} icon={Mail} color="rose" />
        <StatCard label="Unique Gmails" value={stats?.unique_gmails ?? 0} icon={Mail} color="rose" />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Panel title="Top Countries">
          <BarList items={stats?.top_countries || []} labelKey="country" valueKey="count" color="purple" />
        </Panel>
        <Panel title="Top Events">
          <BarList items={stats?.top_events || []} labelKey="event_name" valueKey="count" color="cyan" />
        </Panel>
        <Panel title="Top Pages">
          <BarList items={stats?.top_pages || []} labelKey="page_path" valueKey="count" color="pink" />
        </Panel>
        <Panel title="Top Browsers">
          <BarList items={stats?.top_browsers || []} labelKey="browser" valueKey="count" color="amber" />
        </Panel>
        <Panel title="By Device Type">
          <BarList items={stats?.by_device || []} labelKey="device_type" valueKey="count" color="emerald" />
        </Panel>
        <Panel title="Events Timeline (24h)">
          <Timeline data={stats?.timeline_24h || []} />
        </Panel>
      </div>
    </div>
  )
}

// ============================================================
// Live Feed Tab
// ============================================================
function LiveTab() {
  const [events, setEvents] = useState<any[]>([])
  const [paused, setPaused] = useState(false)

  useEffect(() => {
    let mounted = true
    const poll = async () => {
      if (paused) return
      try {
        const r = await api.live()
        if (mounted) setEvents(r.events)
      } catch {}
    }
    poll()
    const iv = setInterval(poll, 2000)
    return () => { mounted = false; clearInterval(iv) }
  }, [paused])

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="flex h-2 w-2 rounded-full bg-red-500 animate-pulse" />
          <span className="text-sm font-semibold">Live Stream · {events.length} events</span>
        </div>
        <button
          onClick={() => setPaused((p) => !p)}
          className="rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-xs hover:bg-white/10 transition"
        >
          {paused ? '▶ Resume' : '⏸ Pause'}
        </button>
      </div>
      <div className="overflow-hidden rounded-2xl border border-white/10 bg-white/[0.02]">
        <AnimatePresence initial={false}>
          {events.map((e) => (
            <motion.div
              key={e.id}
              layout
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0 }}
              className="border-b border-white/5 px-4 py-3 last:border-0 hover:bg-white/[0.03] transition"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="inline-flex rounded-md bg-nebula-purple/20 px-2 py-0.5 text-xs font-mono text-nebula-purple">
                      {e.event_name}
                    </span>
                    <span className="text-xs text-star-white/40">#{e.id}</span>
                  </div>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-star-white/60">
                    {e.ip_address && <span className="font-mono">{e.ip_address}</span>}
                    {e.country && <span>📍 {e.city || '?'}, {e.country}</span>}
                    {e.browser && <span>🌐 {e.browser}</span>}
                    {e.os && <span>💻 {e.os}</span>}
                    {e.device_type && <span>📱 {e.device_type}</span>}
                    {e.page_path && <span className="font-mono text-cyan-400">{e.page_path}</span>}
                  </div>
                  {e.event_data && Object.keys(e.event_data).length > 0 && (
                    <pre className="mt-1.5 overflow-x-auto rounded-md bg-black/30 p-2 text-[11px] text-green-300 font-mono">
                      {JSON.stringify(e.event_data, null, 2)}
                    </pre>
                  )}
                </div>
                <div className="text-xs text-star-white/40 whitespace-nowrap">
                  {new Date(e.created_at).toLocaleTimeString()}
                </div>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
        {events.length === 0 && <Empty label="Waiting for events..." />}
      </div>
    </div>
  )
}

// ============================================================
// Events Tab
// ============================================================
function EventsTab() {
  const [data, setData] = useState<any>({ events: [], total: 0 })
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [eventFilter, setEventFilter] = useState('')
  const [loading, setLoading] = useState(false)
  const [detail, setDetail] = useState<any>(null)
  const limit = 50

  const load = () => {
    setLoading(true)
    api.events({ page, limit, search, event_name: eventFilter || undefined })
      .then(setData).finally(() => setLoading(false))
  }
  useEffect(() => { load() }, [page, search, eventFilter])

  const totalPages = Math.ceil(data.total / limit)

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-star-white/40" />
          <input
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1) }}
            placeholder="Search IP, path, event..."
            className="w-full rounded-xl border border-white/10 bg-white/5 py-2.5 pl-10 pr-4 text-sm placeholder:text-star-white/30 focus:border-nebula-purple/50 focus:outline-none"
          />
        </div>
        <input
          value={eventFilter}
          onChange={(e) => { setEventFilter(e.target.value); setPage(1) }}
          placeholder="Filter event name"
          className="rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 text-sm placeholder:text-star-white/30 focus:border-nebula-purple/50 focus:outline-none"
        />
        <div className="text-xs text-star-white/50">
          {data.total.toLocaleString()} total
        </div>
      </div>

      <div className="overflow-x-auto rounded-2xl border border-white/10 bg-white/[0.02]">
        <table className="w-full text-sm">
          <thead className="border-b border-white/5 bg-white/[0.03] text-left text-xs uppercase text-star-white/50">
            <tr>
              <th className="px-4 py-3">ID</th>
              <th className="px-4 py-3">Time</th>
              <th className="px-4 py-3">Event</th>
              <th className="px-4 py-3">IP</th>
              <th className="px-4 py-3">Location</th>
              <th className="px-4 py-3">Device</th>
              <th className="px-4 py-3">Page</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {data.events.map((e: any) => (
              <tr key={e.id} className="border-b border-white/5 last:border-0 hover:bg-white/[0.03]">
                <td className="px-4 py-3 font-mono text-xs text-star-white/40">#{e.id}</td>
                <td className="px-4 py-3 text-xs text-star-white/60 whitespace-nowrap">
                  {new Date(e.created_at).toLocaleString()}
                </td>
                <td className="px-4 py-3">
                  <span className="inline-flex rounded-md bg-nebula-purple/20 px-2 py-0.5 text-xs font-mono text-nebula-purple">
                    {e.event_name}
                  </span>
                </td>
                <td className="px-4 py-3 font-mono text-xs">{e.ip_address || '-'}</td>
                <td className="px-4 py-3 text-xs">
                  {e.country ? `${e.city || '?'}, ${e.country}` : '-'}
                </td>
                <td className="px-4 py-3 text-xs">
                  <DeviceIcon type={e.device_type} />
                  <span className="ml-1">{e.browser || '-'} / {e.os || '-'}</span>
                </td>
                <td className="px-4 py-3 font-mono text-xs text-cyan-400 truncate max-w-[200px]">
                  {e.page_path || '-'}
                </td>
                <td className="px-4 py-3">
                  <button
                    onClick={() => api.eventDetail(e.id).then(setDetail)}
                    className="rounded-md p-1.5 hover:bg-white/10 transition"
                  >
                    <Eye className="h-4 w-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {data.events.length === 0 && !loading && <Empty label="No events found" />}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-star-white/50">Page {page} of {totalPages}</span>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="rounded-lg border border-white/10 bg-white/5 p-2 hover:bg-white/10 disabled:opacity-40 transition"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="rounded-lg border border-white/10 bg-white/5 p-2 hover:bg-white/10 disabled:opacity-40 transition"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      <AnimatePresence>
        {detail && <EventDetailModal event={detail} onClose={() => setDetail(null)} />}
      </AnimatePresence>
    </div>
  )
}

// ============================================================
// Visitors Tab
// ============================================================
function VisitorsTab() {
  const [data, setData] = useState<any>({ visitors: [] })
  const [loading, setLoading] = useState(true)
  const [detail, setDetail] = useState<any>(null)
  const [detailLoading, setDetailLoading] = useState(false)

  useEffect(() => { api.visitors().then(setData).finally(() => setLoading(false)) }, [])

  const openDetail = async (visitorId: string) => {
    setDetailLoading(true)
    setDetail({ loading: true, visitor_id: visitorId })
    try {
      const d = await api.visitorDetail(visitorId)
      setDetail({ ...d, visitor_id: visitorId })
    } catch (e: any) {
      setDetail({ error: e.message, visitor_id: visitorId })
    } finally {
      setDetailLoading(false)
    }
  }

  if (loading) return <Skeleton />

  return (
    <>
      <div className="overflow-hidden rounded-2xl border border-white/10 bg-white/[0.02]">
        <table className="w-full text-sm">
          <thead className="border-b border-white/5 bg-white/[0.03] text-left text-xs uppercase text-star-white/50">
            <tr>
              <th className="px-4 py-3">Visitor ID</th>
              <th className="px-4 py-3">Events</th>
              <th className="px-4 py-3">Sessions</th>
              <th className="px-4 py-3">Location</th>
              <th className="px-4 py-3">IP</th>
              <th className="px-4 py-3">Device</th>
              <th className="px-4 py-3">First Seen</th>
              <th className="px-4 py-3">Last Seen</th>
            </tr>
          </thead>
          <tbody>
            {data.visitors.map((v: any) => (
              <tr
                key={v.visitor_id}
                onClick={() => openDetail(v.visitor_id)}
                className="cursor-pointer border-b border-white/5 last:border-0 hover:bg-white/[0.05] transition"
              >
                <td className="px-4 py-3 font-mono text-xs text-nebula-purple">{v.visitor_id?.slice(0, 12)}…</td>
                <td className="px-4 py-3 font-semibold">{v.event_count}</td>
                <td className="px-4 py-3">{v.session_count}</td>
                <td className="px-4 py-3 text-xs">
                  {v.country ? `${v.city || '?'}, ${v.country}` : '-'}
                </td>
                <td className="px-4 py-3 font-mono text-xs">{v.ip_address || '-'}</td>
                <td className="px-4 py-3 text-xs">
                  <DeviceIcon type={v.device_type} />
                  <span className="ml-1">{v.browser || '-'} / {v.os || '-'}</span>
                </td>
                <td className="px-4 py-3 text-xs text-star-white/60">
                  {v.first_seen ? new Date(v.first_seen).toLocaleString() : '-'}
                </td>
                <td className="px-4 py-3 text-xs text-star-white/60">
                  {v.last_seen ? new Date(v.last_seen).toLocaleString() : '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {data.visitors.length === 0 && <Empty label="No visitors yet" />}
      </div>

      <AnimatePresence>
        {detail && (
          <VisitorDetailModal
            data={detail}
            loading={detailLoading}
            onClose={() => setDetail(null)}
          />
        )}
      </AnimatePresence>
    </>
  )
}

// ============================================================
// Gmail Tab
// ============================================================
function GmailTab() {
  const [data, setData] = useState<any>({ logins: [] })
  const [loading, setLoading] = useState(true)
  const [detail, setDetail] = useState<any>(null)
  const [detailLoading, setDetailLoading] = useState(false)

  useEffect(() => { api.gmailLogins().then(setData).finally(() => setLoading(false)) }, [])

  const openDetail = async (email: string) => {
    setDetailLoading(true)
    setDetail({ loading: true, email })
    try {
      const d = await api.gmailByEmail(email)
      setDetail({ ...d, email })
    } catch (e: any) {
      setDetail({ error: e.message, email })
    } finally {
      setDetailLoading(false)
    }
  }

  if (loading) return <Skeleton />

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-yellow-500/20 bg-yellow-500/5 p-4 text-sm">
        <strong className="text-yellow-300">⚠ Sensitive data:</strong>{' '}
        <span className="text-yellow-100/80">
          Every Gmail / Google OAuth login is captured here. Click any card to view full login history, all past IPs, devices, and linked events.
        </span>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {data.logins.map((l: any) => (
          <div
            key={l.id}
            onClick={() => openDetail(l.email)}
            className="cursor-pointer rounded-2xl border border-white/10 bg-white/[0.02] p-5 hover:border-nebula-purple/40 hover:bg-white/[0.04] transition"
          >
            <div className="mb-4 flex items-center gap-3">
              {l.picture ? (
                <img src={l.picture} alt={l.name} className="h-12 w-12 rounded-full border-2 border-white/10" />
              ) : (
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-cosmic text-lg font-bold">
                  {(l.email || '?')[0].toUpperCase()}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="truncate font-semibold">{l.name || l.email}</div>
                <div className="truncate text-xs text-star-white/50">{l.email}</div>
              </div>
              {l.email_verified && (
                <span className="rounded-full bg-green-500/20 px-2 py-0.5 text-[10px] font-bold text-green-400">✓</span>
              )}
            </div>
            <div className="space-y-1.5 text-xs text-star-white/70">
              <InfoLine icon={MapPin} text={l.country ? `${l.city || '?'}, ${l.country}` : 'Unknown'} />
              <InfoLine icon={Globe2} text={l.ip_address || '-'} mono />
              <InfoLine icon={Chrome} text={`${l.browser || '-'} · ${l.os || '-'}`} />
              <InfoLine icon={Clock} text={new Date(l.created_at).toLocaleString()} />
            </div>
          </div>
        ))}
      </div>
      {data.logins.length === 0 && <Empty label="No Gmail logins captured yet" />}

      <AnimatePresence>
        {detail && (
          <GmailDetailModal
            data={detail}
            loading={detailLoading}
            onClose={() => setDetail(null)}
          />
        )}
      </AnimatePresence>
    </div>
  )
}

// ============================================================
// Countries Tab
// ============================================================
function CountriesTab() {
  const [data, setData] = useState<any>({ countries: [] })
  const [loading, setLoading] = useState(true)
  useEffect(() => { api.countries().then(setData).finally(() => setLoading(false)) }, [])

  if (loading) return <Skeleton />
  const max = Math.max(...(data.countries.map((c: any) => c.events) || [1]), 1)

  return (
    <div className="overflow-hidden rounded-2xl border border-white/10 bg-white/[0.02]">
      <table className="w-full text-sm">
        <thead className="border-b border-white/5 bg-white/[0.03] text-left text-xs uppercase text-star-white/50">
          <tr>
            <th className="px-4 py-3">Country</th>
            <th className="px-4 py-3">Events</th>
            <th className="px-4 py-3">Visitors</th>
            <th className="px-4 py-3 w-[40%]">Distribution</th>
            <th className="px-4 py-3">Last Seen</th>
          </tr>
        </thead>
        <tbody>
          {data.countries.map((c: any) => (
            <tr key={c.country} className="border-b border-white/5 last:border-0 hover:bg-white/[0.03]">
              <td className="px-4 py-3 font-semibold">🌍 {c.country}</td>
              <td className="px-4 py-3">{c.events}</td>
              <td className="px-4 py-3">{c.visitors}</td>
              <td className="px-4 py-3">
                <div className="h-2 rounded-full bg-white/5 overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-nebula-purple to-cosmic-blue"
                    style={{ width: `${(c.events / max) * 100}%` }}
                  />
                </div>
              </td>
              <td className="px-4 py-3 text-xs text-star-white/60">
                {new Date(c.last_seen).toLocaleString()}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {data.countries.length === 0 && <Empty label="No geo data yet" />}
    </div>
  )
}

// ============================================================
// Helpers
// ============================================================
function StatCard({
  label, value, icon: Icon, color,
}: { label: string; value: number; icon: any; color: string }) {
  const colorMap: Record<string, string> = {
    purple: 'from-nebula-purple to-purple-600',
    cyan: 'from-aurora-cyan to-cyan-600',
    pink: 'from-pink-500 to-rose-600',
    amber: 'from-amber-500 to-orange-600',
    emerald: 'from-emerald-500 to-teal-600',
    rose: 'from-rose-500 to-pink-600',
  }
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.02] p-5"
    >
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs text-star-white/50 uppercase tracking-wider">{label}</div>
          <div className="mt-2 text-3xl font-bold">{value.toLocaleString()}</div>
        </div>
        <div className={`flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br ${colorMap[color]} shadow-lg`}>
          <Icon className="h-5 w-5 text-white" />
        </div>
      </div>
    </motion.div>
  )
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
      <h3 className="mb-4 text-sm font-semibold uppercase tracking-wider text-star-white/60">{title}</h3>
      {children}
    </div>
  )
}

function BarList({
  items, labelKey, valueKey, color,
}: { items: any[]; labelKey: string; valueKey: string; color: string }) {
  const max = Math.max(...items.map((i) => i[valueKey] || 0), 1)
  const colorMap: Record<string, string> = {
    purple: 'from-nebula-purple to-purple-500',
    cyan: 'from-aurora-cyan to-cyan-500',
    pink: 'from-pink-500 to-rose-500',
    amber: 'from-amber-500 to-orange-500',
    emerald: 'from-emerald-500 to-teal-500',
  }
  if (!items.length) return <div className="text-sm text-star-white/40 py-6 text-center">No data</div>
  return (
    <div className="space-y-2.5">
      {items.map((item, i) => (
        <div key={i}>
          <div className="mb-1 flex justify-between text-xs">
            <span className="truncate text-star-white/80">{item[labelKey] || 'unknown'}</span>
            <span className="font-semibold text-star-white">{item[valueKey]}</span>
          </div>
          <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
            <div
              className={`h-full bg-gradient-to-r ${colorMap[color]}`}
              style={{ width: `${(item[valueKey] / max) * 100}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  )
}

function Timeline({ data }: { data: { hour: string; count: number }[] }) {
  if (!data.length) return <div className="text-sm text-star-white/40 py-6 text-center">No data</div>
  const max = Math.max(...data.map((d) => d.count), 1)
  return (
    <div className="flex h-32 items-end gap-1">
      {data.map((d, i) => (
        <div key={i} className="flex-1 group relative">
          <div
            className="w-full rounded-t bg-gradient-to-t from-nebula-purple to-cosmic-blue transition hover:opacity-80"
            style={{ height: `${(d.count / max) * 100}%`, minHeight: d.count ? '4px' : '0' }}
            title={`${new Date(d.hour).toLocaleTimeString()}: ${d.count}`}
          />
        </div>
      ))}
    </div>
  )
}

function DeviceIcon({ type }: { type: string }) {
  if (type === 'mobile') return <Smartphone className="inline h-3.5 w-3.5 text-cyan-400" />
  if (type === 'tablet') return <TabletSmartphone className="inline h-3.5 w-3.5 text-purple-400" />
  if (type === 'desktop') return <Monitor className="inline h-3.5 w-3.5 text-emerald-400" />
  return <Monitor className="inline h-3.5 w-3.5 text-star-white/40" />
}

function InfoLine({ icon: Icon, text, mono }: { icon: any; text: string; mono?: boolean }) {
  return (
    <div className="flex items-center gap-2">
      <Icon className="h-3 w-3 text-star-white/40 flex-shrink-0" />
      <span className={mono ? 'font-mono' : ''}>{text}</span>
    </div>
  )
}

function Empty({ label }: { label: string }) {
  return <div className="p-12 text-center text-sm text-star-white/40">{label}</div>
}

function Skeleton() {
  return (
    <div className="space-y-4">
      {[1, 2, 3].map((i) => (
        <div key={i} className="h-24 rounded-2xl bg-white/5 animate-pulse" />
      ))}
    </div>
  )
}

function VisitorDetailModal({ data, loading, onClose }: { data: any; loading: boolean; onClose: () => void }) {
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="relative max-h-[90vh] w-full max-w-5xl overflow-auto rounded-2xl border border-white/10 bg-void-black"
      >
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-white/10 bg-void-black/95 px-6 py-4 backdrop-blur">
          <div>
            <div className="text-xs uppercase tracking-wider text-star-white/50">Visitor</div>
            <div className="font-mono text-lg font-bold text-nebula-purple">{data.visitor_id}</div>
          </div>
          <button onClick={onClose} className="rounded-full p-2 hover:bg-white/10 transition">
            <X className="h-5 w-5" />
          </button>
        </div>

        {loading && <div className="p-12 text-center text-star-white/50">Loading…</div>}
        {data.error && <div className="p-12 text-center text-red-400">{data.error}</div>}

        {data.summary && (
          <div className="space-y-6 p-6">
            <div className="grid gap-3 md:grid-cols-3">
              <MetaBox label="Total Events" value={data.summary.total_events} />
              <MetaBox label="Sessions" value={data.summary.sessions} />
              <MetaBox label="First Seen" value={data.summary.first_seen ? new Date(data.summary.first_seen).toLocaleString() : '-'} small />
              <MetaBox label="Last Seen" value={data.summary.last_seen ? new Date(data.summary.last_seen).toLocaleString() : '-'} small />
              <MetaBox label="Countries" value={(data.summary.countries || []).join(', ') || '-'} small />
              <MetaBox label="Cities" value={(data.summary.cities || []).join(', ') || '-'} small />
              <MetaBox label="IPs Seen" value={(data.summary.ips || []).join(', ') || '-'} small mono />
              <MetaBox label="Browsers" value={(data.summary.browsers || []).join(', ') || '-'} small />
              <MetaBox label="OS / Family" value={`${(data.summary.operating_systems || []).join(', ') || '-'} · ${(data.summary.os_families || []).join(', ') || '-'}`} small />
              <MetaBox label="Device Types" value={(data.summary.device_types || []).join(', ') || '-'} small />
              <MetaBox label="GPUs" value={(data.summary.gpus || []).join(', ') || '-'} small />
              <MetaBox label="Canvas FPs" value={(data.summary.canvas_fps || []).join(', ') || '-'} small mono />
            </div>

            {/* ─────── Device Intelligence (latest event snapshot) ─────── */}
            {data.events && data.events[0] && (
              <section>
                <h3 className="mb-3 text-sm font-bold uppercase tracking-wider text-star-white/60">
                  Device Intelligence <span className="text-star-white/40">(latest snapshot)</span>
                </h3>
                <DeviceIntelligenceGrid ev={data.events[0]} />
              </section>
            )}

            {data.gmail_logins && data.gmail_logins.length > 0 && (
              <section>
                <h3 className="mb-3 text-sm font-bold uppercase tracking-wider text-star-white/60">Linked Gmail Logins ({data.gmail_logins.length})</h3>
                <div className="space-y-2">
                  {data.gmail_logins.map((g: any) => (
                    <div key={g.id} className="flex items-center gap-3 rounded-xl border border-white/5 bg-white/5 p-3">
                      {g.picture ? <img src={g.picture} className="h-8 w-8 rounded-full" /> : null}
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold truncate">{g.name || g.email}</div>
                        <div className="text-xs text-star-white/50 truncate">{g.email}</div>
                      </div>
                      <div className="text-xs text-star-white/60 text-right whitespace-nowrap">
                        <div>{new Date(g.created_at).toLocaleString()}</div>
                        <div className="font-mono">{g.ip_address}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            <section>
              <h3 className="mb-3 text-sm font-bold uppercase tracking-wider text-star-white/60">Event History ({data.events?.length || 0})</h3>
              <div className="overflow-hidden rounded-xl border border-white/10">
                <table className="w-full text-xs">
                  <thead className="bg-white/5 text-left uppercase text-star-white/50">
                    <tr>
                      <th className="px-3 py-2">Time</th>
                      <th className="px-3 py-2">Event</th>
                      <th className="px-3 py-2">IP</th>
                      <th className="px-3 py-2">Location</th>
                      <th className="px-3 py-2">Geo (lat/lng)</th>
                      <th className="px-3 py-2">Device</th>
                      <th className="px-3 py-2">Page</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data.events || []).map((e: any) => (
                      <tr key={e.id} className="border-t border-white/5 hover:bg-white/[0.03]">
                        <td className="px-3 py-1.5 whitespace-nowrap text-star-white/60">{new Date(e.created_at).toLocaleTimeString()}</td>
                        <td className="px-3 py-1.5"><span className="rounded bg-nebula-purple/20 px-1.5 py-0.5 font-mono text-[10px] text-nebula-purple">{e.event_name}</span></td>
                        <td className="px-3 py-1.5 font-mono">{e.ip_address || '-'}</td>
                        <td className="px-3 py-1.5">{e.country ? `${e.city || '?'}, ${e.country}` : '-'}</td>
                        <td className="px-3 py-1.5 font-mono text-[10px] text-emerald-400">
                          {e.geo_latitude ? `${e.geo_latitude.toFixed(4)}, ${e.geo_longitude.toFixed(4)}` : '-'}
                        </td>
                        <td className="px-3 py-1.5">{e.browser || '-'} / {e.os || '-'} · {e.device_type}</td>
                        <td className="px-3 py-1.5 font-mono text-cyan-400 truncate max-w-[160px]">{e.page_path || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </div>
        )}
      </motion.div>
    </motion.div>
  )
}

function GmailDetailModal({ data, loading, onClose }: { data: any; loading: boolean; onClose: () => void }) {
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="relative max-h-[90vh] w-full max-w-5xl overflow-auto rounded-2xl border border-white/10 bg-void-black"
      >
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-white/10 bg-void-black/95 px-6 py-4 backdrop-blur">
          <div className="flex items-center gap-3">
            {data.logins?.[0]?.picture && <img src={data.logins[0].picture} className="h-10 w-10 rounded-full" />}
            <div>
              <div className="font-bold">{data.logins?.[0]?.name || data.email}</div>
              <div className="text-xs text-star-white/50">{data.email}</div>
            </div>
          </div>
          <button onClick={onClose} className="rounded-full p-2 hover:bg-white/10 transition">
            <X className="h-5 w-5" />
          </button>
        </div>

        {loading && <div className="p-12 text-center text-star-white/50">Loading…</div>}
        {data.error && <div className="p-12 text-center text-red-400">{data.error}</div>}

        {data.summary && (
          <div className="space-y-6 p-6">
            <div className="grid gap-3 md:grid-cols-3">
              <MetaBox label="Total Logins" value={data.summary.total_logins} />
              <MetaBox label="First Login" value={data.summary.first_login ? new Date(data.summary.first_login).toLocaleString() : '-'} small />
              <MetaBox label="Last Login" value={data.summary.last_login ? new Date(data.summary.last_login).toLocaleString() : '-'} small />
              <MetaBox label="Countries" value={(data.summary.countries || []).join(', ') || '-'} small />
              <MetaBox label="Cities" value={(data.summary.cities || []).join(', ') || '-'} small />
              <MetaBox label="IPs" value={(data.summary.ips || []).join(', ') || '-'} small mono />
              <MetaBox label="Browsers" value={(data.summary.browsers || []).join(', ') || '-'} small />
              <MetaBox label="Operating Systems" value={(data.summary.operating_systems || []).join(', ') || '-'} small />
              <MetaBox label="Device Types" value={(data.summary.device_types || []).join(', ') || '-'} small />
              <MetaBox label="Visitors Linked" value={(data.summary.visitors || []).length} />
            </div>

            <section>
              <h3 className="mb-3 text-sm font-bold uppercase tracking-wider text-star-white/60">Login History ({data.logins.length})</h3>
              <div className="overflow-hidden rounded-xl border border-white/10">
                <table className="w-full text-xs">
                  <thead className="bg-white/5 text-left uppercase text-star-white/50">
                    <tr>
                      <th className="px-3 py-2">Time</th>
                      <th className="px-3 py-2">IP</th>
                      <th className="px-3 py-2">Location</th>
                      <th className="px-3 py-2">ISP</th>
                      <th className="px-3 py-2">Device</th>
                      <th className="px-3 py-2">OS Family</th>
                      <th className="px-3 py-2">Referer</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.logins.map((l: any) => (
                      <tr key={l.id} className="border-t border-white/5 hover:bg-white/[0.03]">
                        <td className="px-3 py-1.5 whitespace-nowrap text-star-white/60">{new Date(l.created_at).toLocaleString()}</td>
                        <td className="px-3 py-1.5 font-mono">{l.ip_address || '-'}</td>
                        <td className="px-3 py-1.5">{l.country ? `${l.city || '?'}, ${l.country}` : '-'}</td>
                        <td className="px-3 py-1.5 text-star-white/60">{l.isp || '-'}</td>
                        <td className="px-3 py-1.5">{l.browser || '-'} / {l.os || '-'}</td>
                        <td className="px-3 py-1.5">{l.device_type || '-'}</td>
                        <td className="px-3 py-1.5 font-mono truncate max-w-[160px] text-cyan-400">{l.referer || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            {data.linked_events && data.linked_events.length > 0 && (
              <section>
                <h3 className="mb-3 text-sm font-bold uppercase tracking-wider text-star-white/60">Recent Activity ({data.linked_events.length})</h3>
                <div className="overflow-hidden rounded-xl border border-white/10">
                  <table className="w-full text-xs">
                    <thead className="bg-white/5 text-left uppercase text-star-white/50">
                      <tr>
                        <th className="px-3 py-2">Time</th>
                        <th className="px-3 py-2">Event</th>
                        <th className="px-3 py-2">IP</th>
                        <th className="px-3 py-2">Page</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.linked_events.map((e: any) => (
                        <tr key={e.id} className="border-t border-white/5">
                          <td className="px-3 py-1.5 whitespace-nowrap text-star-white/60">{new Date(e.created_at).toLocaleString()}</td>
                          <td className="px-3 py-1.5"><span className="rounded bg-nebula-purple/20 px-1.5 py-0.5 font-mono text-[10px] text-nebula-purple">{e.event_name}</span></td>
                          <td className="px-3 py-1.5 font-mono">{e.ip_address || '-'}</td>
                          <td className="px-3 py-1.5 font-mono text-cyan-400 truncate max-w-[160px]">{e.page_path || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}
          </div>
        )}
      </motion.div>
    </motion.div>
  )
}

function MetaBox({ label, value, small, mono }: { label: string; value: any; small?: boolean; mono?: boolean }) {
  return (
    <div className="rounded-xl border border-white/5 bg-white/[0.02] p-3">
      <div className="text-[10px] uppercase tracking-wider text-star-white/50">{label}</div>
      <div className={`mt-1 ${small ? 'text-sm' : 'text-xl font-bold'} ${mono ? 'font-mono' : ''} break-words`}>{String(value ?? '-')}</div>
    </div>
  )
}

// ============================================================
// DeviceIntelligenceGrid — every capture-able field grouped
// ============================================================
function DeviceIntelligenceGrid({ ev }: { ev: any }) {
  const mapsUrl = ev.geo_latitude != null && ev.geo_longitude != null
    ? `https://www.google.com/maps?q=${ev.geo_latitude},${ev.geo_longitude}`
    : null
  const ipMapsUrl = ev.latitude != null && ev.longitude != null
    ? `https://www.google.com/maps?q=${ev.latitude},${ev.longitude}`
    : null
  const osCh = ev.os_ua_ch && typeof ev.os_ua_ch === 'object' ? ev.os_ua_ch : null

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
      {/* OS + platform */}
      <IntelCard title="Operating System" tone="purple">
        <KV k="Family" v={ev.os_family} mono />
        <KV k="Name" v={`${ev.os || '-'} ${ev.os_version || ''}`} />
        <KV k="Platform" v={ev.platform} />
        {osCh && <>
          <KV k="Architecture" v={`${osCh.architecture || '-'} ${osCh.bitness || ''}`} />
          <KV k="Platform version" v={osCh.platformVersion} />
          <KV k="Model (UA-CH)" v={osCh.model} />
          <KV k="Form factor" v={osCh.formFactor} />
        </>}
      </IntelCard>

      {/* Browser */}
      <IntelCard title="Browser">
        <KV k="Browser" v={`${ev.browser || '-'} ${ev.browser_version || ''}`} />
        <KV k="Vendor" v={ev.vendor} />
        <KV k="Webdriver" v={ev.webdriver === true ? '🤖 YES' : 'no'} danger={ev.webdriver} />
        <KV k="Bot detected" v={ev.is_bot === true ? '🤖 YES' : 'no'} danger={ev.is_bot} />
        <KV k="PDF viewer" v={ev.pdf_viewer_enabled ? 'yes' : 'no'} />
        <KV k="Cookies" v={ev.cookies_enabled ? 'on' : 'off'} />
        <KV k="Do Not Track" v={ev.do_not_track} />
      </IntelCard>

      {/* Device */}
      <IntelCard title="Device">
        <KV k="Type" v={ev.device_type} />
        <KV k="Brand" v={ev.device_brand} />
        <KV k="Model" v={ev.device_model} />
        <KV k="Max touch" v={ev.max_touch_points} />
        <KV k="Touch support" v={ev.touch_support ? 'yes' : 'no'} />
        <KV k="Pointer" v={ev.pointer_coarse ? 'coarse (touch)' : 'fine (mouse)'} />
        <KV k="Hover" v={ev.hover_support ? 'yes' : 'no'} />
      </IntelCard>

      {/* Hardware */}
      <IntelCard title="Hardware">
        <KV k="CPU cores" v={ev.hardware_concurrency} />
        <KV k="RAM (GB)" v={ev.device_memory} />
        <KV k="GPU vendor" v={ev.gpu_vendor} small />
        <KV k="GPU renderer" v={ev.gpu_renderer} small />
        <KV k="WebGL" v={ev.webgl_version} small />
      </IntelCard>

      {/* Battery */}
      <IntelCard title="Battery" tone={ev.battery_charging ? 'emerald' : undefined}>
        {ev.battery_level != null ? (
          <>
            <KV k="Level" v={`${Math.round(ev.battery_level * 100)}% ${ev.battery_charging ? '⚡ charging' : ''}`} />
            <KV k="Charging time" v={ev.battery_charging_time && ev.battery_charging_time > 0 ? `${Math.round(ev.battery_charging_time / 60)} min` : '-'} />
            <KV k="Time remaining" v={ev.battery_discharging_time && ev.battery_discharging_time > 0 ? `${(ev.battery_discharging_time / 3600).toFixed(1)} h` : '-'} />
          </>
        ) : <div className="text-xs text-star-white/40">Battery API unavailable / blocked</div>}
      </IntelCard>

      {/* Display */}
      <IntelCard title="Display">
        <KV k="Screen" v={`${ev.screen_width}×${ev.screen_height}`} />
        <KV k="Available" v={ev.screen_avail_width ? `${ev.screen_avail_width}×${ev.screen_avail_height}` : '-'} />
        <KV k="Viewport" v={`${ev.viewport_width}×${ev.viewport_height}`} />
        <KV k="Pixel ratio" v={ev.pixel_ratio} />
        <KV k="Color depth" v={`${ev.color_depth}-bit`} />
        <KV k="Orientation" v={ev.screen_orientation} />
      </IntelCard>

      {/* Network */}
      <IntelCard title="Network">
        <KV k="Public IP" v={ev.ip_address} mono />
        <KV k="Local (WebRTC)" v={ev.webrtc_local_ip} mono />
        <KV k="ISP" v={ev.isp} small />
        <KV k="ASN" v={ev.asn} small />
        <KV k="Type" v={ev.connection_type} />
        <KV k="Downlink" v={ev.connection_downlink ? `${ev.connection_downlink} Mbps` : '-'} />
        <KV k="RTT" v={ev.connection_rtt ? `${ev.connection_rtt} ms` : '-'} />
        <KV k="Save data" v={ev.network_save_data ? 'on' : 'off'} />
      </IntelCard>

      {/* Location (IP-based) */}
      <IntelCard title="Location (IP geo)">
        <KV k="Country" v={ev.country} />
        <KV k="Region" v={ev.region} />
        <KV k="City" v={ev.city} />
        <KV k="Postal" v={ev.postal_code} />
        <KV k="Lat/Lng" v={ev.latitude != null ? `${ev.latitude}, ${ev.longitude}` : '-'} mono small />
        {ipMapsUrl && <a href={ipMapsUrl} target="_blank" rel="noopener" className="mt-1 inline-flex items-center gap-1 rounded-full bg-cosmic-blue/20 px-2 py-0.5 text-[10px] font-semibold text-cosmic-blue hover:bg-cosmic-blue/30">View on map →</a>}
      </IntelCard>

      {/* Location (precise GPS) */}
      <IntelCard title="Precise GPS" tone={ev.geo_latitude != null ? 'emerald' : undefined}>
        {ev.geo_latitude != null ? (
          <>
            <KV k="Lat/Lng" v={`${ev.geo_latitude.toFixed(6)}, ${ev.geo_longitude.toFixed(6)}`} mono />
            <KV k="Accuracy" v={ev.geo_accuracy_m != null ? `± ${Math.round(ev.geo_accuracy_m)} m` : '-'} />
            <KV k="Altitude" v={ev.geo_altitude != null ? `${ev.geo_altitude} m` : '-'} />
            <KV k="Heading" v={ev.geo_heading != null ? `${ev.geo_heading}°` : '-'} />
            <KV k="Speed" v={ev.geo_speed != null ? `${ev.geo_speed} m/s` : '-'} />
            {mapsUrl && <a href={mapsUrl} target="_blank" rel="noopener" className="mt-1 inline-flex items-center gap-1 rounded-full bg-emerald-500/20 px-2 py-0.5 text-[10px] font-semibold text-emerald-400 hover:bg-emerald-500/30">Open in Google Maps →</a>}
          </>
        ) : <div className="text-xs text-star-white/40">Permission: {ev.geo_permission || 'unknown'}</div>}
      </IntelCard>

      {/* Fingerprints */}
      <IntelCard title="Fingerprints" tone="purple">
        <KV k="Canvas FP" v={ev.canvas_fp} mono small />
        <KV k="Audio FP" v={ev.audio_fp} mono small />
        <KV k="Fonts (est.)" v={ev.font_count_estimate} />
        <KV k="Plugins" v={ev.plugin_count} />
      </IntelCard>

      {/* Storage + media */}
      <IntelCard title="Storage & Media">
        <KV k="Storage quota" v={ev.storage_quota_mb != null ? `${ev.storage_quota_mb.toLocaleString()} MB` : '-'} />
        <KV k="Storage used" v={ev.storage_usage_mb != null ? `${ev.storage_usage_mb.toLocaleString()} MB` : '-'} />
        <KV k="Media devices" v={ev.media_device_count} />
        <KV k="Kinds" v={ev.media_kinds} small />
      </IntelCard>

      {/* Memory */}
      <IntelCard title="JS Heap">
        <KV k="Used" v={ev.performance_memory_used_mb != null ? `${ev.performance_memory_used_mb} MB` : '-'} />
        <KV k="Total" v={ev.performance_memory_total_mb != null ? `${ev.performance_memory_total_mb} MB` : '-'} />
        <KV k="Limit" v={ev.performance_memory_limit_mb != null ? `${ev.performance_memory_limit_mb} MB` : '-'} />
      </IntelCard>

      {/* Locale */}
      <IntelCard title="Locale">
        <KV k="Language" v={ev.language} />
        <KV k="All languages" v={ev.languages} small />
        <KV k="Timezone" v={ev.timezone} />
        <KV k="UTC offset" v={ev.timezone_offset != null ? `${-ev.timezone_offset / 60} h` : '-'} />
      </IntelCard>

      {/* Referer / page */}
      <IntelCard title="Page context">
        <KV k="URL" v={ev.page_url} small mono />
        <KV k="Path" v={ev.page_path} mono />
        <KV k="Title" v={ev.page_title} small />
        <KV k="Referer" v={ev.referer} small mono />
      </IntelCard>
    </div>
  )
}

function IntelCard({
  title, children, tone,
}: { title: string; children: React.ReactNode; tone?: 'emerald' | 'purple' }) {
  const ring =
    tone === 'emerald' ? 'ring-1 ring-emerald-500/30'
    : tone === 'purple' ? 'ring-1 ring-nebula-purple/30'
    : ''
  return (
    <div className={`rounded-xl border border-white/10 bg-white/[0.03] p-4 ${ring}`}>
      <div className="mb-2 text-[10px] font-bold uppercase tracking-wider text-star-white/50">{title}</div>
      <div className="space-y-1.5">{children}</div>
    </div>
  )
}

function KV({
  k, v, mono, small, danger,
}: { k: string; v: any; mono?: boolean; small?: boolean; danger?: boolean }) {
  const isEmpty = v == null || v === '' || (Array.isArray(v) && v.length === 0)
  const display = isEmpty
    ? '—'
    : Array.isArray(v) ? v.join(', ')
    : typeof v === 'boolean' ? (v ? 'true' : 'false')
    : String(v)
  return (
    <div className="flex items-start justify-between gap-2 text-xs">
      <span className="shrink-0 text-star-white/50">{k}</span>
      <span className={[
        'text-right break-all',
        mono ? 'font-mono text-[11px]' : '',
        small ? 'text-[11px]' : '',
        isEmpty ? 'text-star-white/30' : danger ? 'text-red-400 font-semibold' : 'text-star-white',
      ].join(' ')}>{display}</span>
    </div>
  )
}

function EventDetailModal({ event, onClose }: { event: any; onClose: () => void }) {
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="relative max-h-[90vh] w-full max-w-6xl overflow-auto rounded-2xl border border-white/10 bg-void-black p-6"
      >
        <button onClick={onClose} className="absolute right-4 top-4 rounded-full p-2 hover:bg-white/10 transition">
          <X className="h-5 w-5" />
        </button>
        <h2 className="mb-4 text-xl font-bold">
          Event #{event.id} · <span className="text-nebula-purple">{event.event_name}</span>
          <span className="ml-2 text-xs font-normal text-star-white/50">{event.created_at ? new Date(event.created_at).toLocaleString() : ''}</span>
        </h2>
        {event.event_data && Object.keys(event.event_data).length > 0 && (
          <div className="mb-4 rounded-xl border border-white/10 bg-white/[0.02] p-4">
            <div className="mb-2 text-[10px] font-bold uppercase tracking-wider text-star-white/50">Event payload</div>
            <pre className="overflow-x-auto text-xs font-mono text-emerald-300">{JSON.stringify(event.event_data, null, 2)}</pre>
          </div>
        )}
        <DeviceIntelligenceGrid ev={event} />
        <details className="mt-4 rounded-xl border border-white/10 bg-black/30 p-3">
          <summary className="cursor-pointer text-xs text-star-white/60">Raw row JSON</summary>
          <pre className="mt-2 overflow-x-auto text-[10px] font-mono text-star-white/60">{JSON.stringify(event, null, 2)}</pre>
        </details>
      </motion.div>
    </motion.div>
  )
}
